'use client';

/**
 * 岗位列表页
 * 
 * @页面路径 /organization/positions
 * @权限要求 position:read:*
 * @文档参考 docs/modules/organization/05-ui-interaction-spec.md
 * 
 * 核心功能：
 * - 岗位列表展示（名称、代码、级别、描述）
 * - 搜索功能（岗位名称/代码）
 * - 岗位CRUD操作（创建、编辑、删除）
 * - 岗位级别管理（L1-L10）
 * - 显示使用该岗位的员工数量
 */

import { useEffect, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useDebounce } from '@/hooks/useDebounce';
import { toast } from '@/lib/toast';
import { useConfirm } from '@/components/common/feedback/ConfirmProvider';
import { PageState } from '@/components/common/feedback/PageState';
import { handleApiError } from '@/lib/error-handler';
import {
  Briefcase,
  Plus,
  Search,
  Users,
  X,
} from 'lucide-react';
import {
  getPositions,
  createPosition,
  updatePosition,
  deletePosition,
  type Position,
  type CreatePositionDto,
  type UpdatePositionDto,
} from '@/services/api/organization';

interface PositionFormData {
  name: string;
  code: string;
  level: number;
  description: string;
}

export default function PositionsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [showModal, setShowModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);
  const [formData, setFormData] = useState<PositionFormData>({
    name: '',
    code: '',
    level: 1,
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPositions();
  }, [debouncedSearchQuery]);

  const loadPositions = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await getPositions({ keyword: debouncedSearchQuery || undefined });
      setPositions(Array.isArray(data) ? data : []);
    } catch (error: any) {
      const errorMessage = error.message || t.organization.positions.loadFailed;
      setLoadError(errorMessage);
      handleApiError(error, t.locale, { prefix: t.organization.positions.loadFailed });
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingPosition(null);
    setFormData({ name: '', code: '', level: 1, description: '' });
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (position: Position) => {
    setEditingPosition(position);
    setFormData({
      name: position.name,
      code: position.code,
      level: position.level || 1,
      description: position.description || '',
    });
    setError(null);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError(t.organization.positions.nameRequired);
      return;
    }

    if (!formData.code.trim()) {
      setError(t.organization.positions.codeRequired);
      return;
    }

    try {
      setSubmitting(true);

      if (editingPosition) {
        // 更新岗位
        const updateData: UpdatePositionDto = {
          name: formData.name,
          level: formData.level,
          description: formData.description || undefined,
        };
        await updatePosition(editingPosition.id, updateData);
        toast.success(t.common.updateSuccess);
      } else {
        // 创建岗位
        const createData: CreatePositionDto = {
          name: formData.name,
          code: formData.code,
          level: formData.level,
          description: formData.description || undefined,
        };
        await createPosition(createData);
        toast.success(t.common.createSuccess);
      }

      setShowModal(false);
      loadPositions();
    } catch (error: any) {
      console.error('Failed to save position:', error);
      setError(error.message || t.common.saveFailed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (position: Position) => {
    const confirmed = await confirm({
      title: t.organization.positions.confirmDeleteTitle,
      description: t.organization.positions.confirmDelete.replace('{name}', position.name),
      confirmText: t.common.confirmDelete,
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deletePosition(position.id);
      toast.success(t.organization.positions.deleteSuccess);
      loadPositions();
    } catch (error: any) {
      console.error('Failed to delete position:', error);
      toast.error(error.error?.message || error.message || t.organization.positions.deleteFailed);
    }
  };

  // ✅ 修复：搜索已由API处理，无需前端过滤
  // const filteredPositions = positions;

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* Title bar with toolbar - Feishu style */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-3">
              {/* Search Input */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t.organization.positions.searchPlaceholder}
                  className="px-3 py-1.5 pl-9 rounded-lg text-sm outline-none transition-all"
                  style={{ 
                    width: '280px',
                    backgroundColor: '#f7f8fa',
                    color: '#1f2329',
                    border: 'none'
                  }}
                  data-testid="position-search-input"
                  onFocus={(e) => {
                    e.target.style.backgroundColor = '#eff0f2';
                    e.target.style.boxShadow = '0 0 0 2px rgba(51, 112, 255, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.backgroundColor = '#f7f8fa';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8f959e' }} data-testid="search-icon" />
                {searchQuery !== debouncedSearchQuery && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="search-loading">
                    <div className="w-4 h-4 rounded-full animate-spin" style={{ borderTop: '2px solid #3370ff', borderRight: '2px solid transparent' }} />
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Add Position Button */}
              <button
                onClick={openCreateModal}
                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white flex items-center transition-all shadow-sm"
                style={{ 
                  backgroundColor: '#3370ff',
                  boxShadow: '0 1px 2px rgba(51, 112, 255, 0.2)'
                }}
                data-action="create-position"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5087ff';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(51, 112, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#3370ff';
                  e.currentTarget.style.boxShadow = '0 1px 2px rgba(51, 112, 255, 0.2)';
                }}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t.organization.positions.add}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-auto">
        <div className="px-6 pt-4 pb-6">
          {/* Positions List */}
          <div className="bg-white rounded-lg overflow-hidden" style={{ border: '1px solid #e5e6eb' }}>
            {loadError ? (
              <div className="p-12 text-center" data-testid="error-state">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef0f0' }}>
                  <X className="w-6 h-6" style={{ color: '#f53f3f' }} />
                </div>
                <p className="mb-4" style={{ color: '#646a73' }}>{loadError}</p>
                <button
                  onClick={loadPositions}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: '#3370ff',
                    color: '#fff'
                  }}
                >
                  {t.common.retry}
                </button>
              </div>
            ) : loading ? (
              <div className="p-12 text-center" data-testid="loading-skeleton">
                <div className="animate-spin rounded-full h-12 w-12 mx-auto" style={{ borderTop: '2px solid #3370ff', borderRight: '2px solid transparent' }}></div>
                <p className="mt-4" style={{ color: '#646a73' }}>{t.common.loading}</p>
              </div>
            ) : positions.length === 0 ? (
              <div className="p-12" data-testid="empty-state">
                <PageState
                  variant="empty"
                  title={searchQuery ? t.organization.positions.noMatchingPositions : t.organization.positions.noData}
                  actionLabel={!searchQuery ? t.organization.positions.addFirst : undefined}
                  onAction={!searchQuery ? openCreateModal : undefined}
                  layout="center"
                  className="border-0 bg-transparent"
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full" data-testid="position-table">
                  <thead style={{ backgroundColor: '#f7f8fa', borderBottom: '1px solid #e5e6eb' }}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                        {t.organization.positions.name}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                        {t.organization.positions.userCount}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium" style={{ color: '#646a73' }}>
                        {t.organization.positions.description}
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium" style={{ color: '#646a73' }}>
                        {t.common.actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{ backgroundColor: '#fff' }}>
                    {positions.map((position) => (
                      <tr 
                        key={position.id} 
                        className="transition-colors"
                        style={{ borderBottom: '1px solid #e5e6eb' }}
                        data-testid="position-row"
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7f8fa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#ede7f6' }}>
                              <Briefcase className="w-4 h-4" style={{ color: '#7c3aed' }} />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium" style={{ color: '#1f2329' }}>{position.name}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm" style={{ color: '#646a73' }}>
                            <Users className="w-4 h-4 mr-1" />
                            {position.userCount || 0}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm truncate max-w-xs" style={{ color: '#646a73' }}>
                            {position.description || '-'}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => openEditModal(position)}
                              className="text-sm font-medium transition-colors"
                              style={{ color: '#3370ff' }}
                              data-action="edit"
                            >
                              {t.common.edit}
                            </button>
                            <button
                              onClick={() => handleDelete(position)}
                              className="text-sm font-medium transition-colors"
                              style={{ color: '#f53f3f' }}
                              data-action="delete"
                            >
                              {t.common.delete}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Statistics */}
          <div className="mt-4 text-sm" style={{ color: '#646a73' }}>
            {t.organization.positions.totalCount.replace('{count}', positions.length.toString())}
          </div>
        </div>
      </div>

      {/* 创建/编辑弹窗 */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white rounded-xl w-full max-w-md mx-4" style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }}>
            <div className="px-6 py-4" style={{ borderBottom: '1px solid #e5e6eb' }}>
              <h3 className="text-lg font-semibold" style={{ color: '#1f2329' }}>
                {editingPosition 
                  ? t.organization.positions.edit
                  : t.organization.positions.add
                }
              </h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 space-y-4">
                {error && (
                  <div className="p-3 text-sm rounded-lg" style={{ backgroundColor: '#fef0f0', color: '#f53f3f' }}>
                    {error}
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                    {t.organization.positions.name} <span style={{ color: '#f53f3f' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                    style={{ 
                      border: '1px solid #e5e6eb',
                      color: '#1f2329'
                    }}
                    placeholder={t.organization.positions.namePlaceholder}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                    {t.organization.positions.code} <span style={{ color: '#f53f3f' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    disabled={!!editingPosition}
                    className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                    style={{ 
                      border: '1px solid #e5e6eb',
                      color: '#1f2329',
                      backgroundColor: editingPosition ? '#f7f8fa' : '#fff',
                      cursor: editingPosition ? 'not-allowed' : 'text'
                    }}
                    placeholder={t.organization.positions.codePlaceholder}
                  />
                  {editingPosition && (
                    <p className="mt-1 text-xs" style={{ color: '#646a73' }}>
                      {t.organization.positions.codeNotEditable}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                    {t.organization.positions.level}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) || 1 })}
                    className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                    style={{ 
                      border: '1px solid #e5e6eb',
                      color: '#1f2329'
                    }}
                  />
                  <p className="mt-1 text-xs" style={{ color: '#646a73' }}>
                    {t.organization.positions.levelTip}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                    {t.organization.positions.description}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                    style={{ 
                      border: '1px solid #e5e6eb',
                      color: '#1f2329'
                    }}
                    placeholder={t.organization.positions.descriptionPlaceholder}
                  />
                </div>
              </div>

              <div className="px-6 py-4 flex justify-end gap-3" style={{ borderTop: '1px solid #e5e6eb' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e6eb',
                    color: '#1f2329'
                  }}
                >
                  {t.common.cancel}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{
                    backgroundColor: submitting ? '#8faeff' : '#3370ff',
                    color: '#fff',
                    border: 'none',
                    opacity: submitting ? 0.5 : 1
                  }}
                >
                  {submitting ? t.common.saving : t.common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
